import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface L1Data {
  id: string | number;
  name: string;
  code: string;
  value?: number;
  children?: L1Data[];
}

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  value?: number;
  l1s?: L1Data[];
}

interface ImportExportReportTableProps {
  reportKey: string;
  filters: any;
}

const ImportExportReportTable: React.FC<ImportExportReportTableProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<DepartmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [consignmentType, setConsignmentType] = useState<string>('');

  const isCountReport = reportKey === 'imp-exp-count-by-departments';
  const isEarningsReport = reportKey === 'imp-exp-earnings-by-departments';

  const getCountColumnTitle = () => {
    if (consignmentType === 'IMPORT') return 'Import Count';
    if (consignmentType === 'EXPORT') return 'Export Count';
    return 'Count';
  };

  const columns = [
    {
      title: isCountReport ? 'Department / Category / Product' : 'Department / Category',
      dataIndex: 'name',
      key: 'name',
    },
    ...(isCountReport
      ? [
          {
            title: getCountColumnTitle(),
            dataIndex: 'value',
            key: 'value',
            render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
          },
        ]
      : [
          {
            title: 'Total (LKR)',
            dataIndex: 'value',
            key: 'value',
            render: (value: number | undefined) =>
              value !== undefined ? value.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-',
          },
        ]),
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await reportApi.generateReport(filters);
        console.log('Filters:', filters);
        // Store consignment type for count reports
        if (isCountReport && filters.type) {
          setConsignmentType(filters.type);
        }

        if (isCountReport) {
          // Transform for count report with department -> category -> product hierarchy
          const departments: DepartmentData[] = (response?.report?.data || []).map((dept: any) => ({
            id: dept.department_id,
            code: dept.department_code,
            name: `${dept.department_code} - ${dept.department_name}`,
            value: dept.total_count,
            l1s: (dept.categories || []).map((category: any) => ({
              id: category.category_id,
              code: category.category_code,
              name: `${category.category_code} - ${category.category_name}`,
              value: category.total_count,
              children: (category.products || []).map((product: any) => ({
                id: product.product_id,
                code: product.product_code,
                name: `${product.product_code} - ${product.product_name}`,
                value: product.count,
              })),
            })),
          }));

          setData(departments);
        } else {
          const departments: DepartmentData[] = Object.values(response?.report || {}).map((dept: any) => ({
            id: dept.id,
            code: dept.code,
            name: `${dept.code} - ${dept.name}`,
            value: dept.total,
            l1s: Object.values(dept.categories || {}).map((category: any) => ({
              id: category.id,
              code: category.code,
              name: `${category.code} - ${category.name}`,
              value: category.total,
            })),
          }));

          setData(departments);
        }
      } catch (err) {
        message.error('Failed to fetch report data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, reportKey, isCountReport]);

  const tableData = data.map((dept) => ({
    key: dept.id,
    name: dept.name,
    value: dept.value,
    children: dept.l1s?.map((l1) => ({
      key: `${dept.id}-${l1.id}`,
      name: `${l1.name}`,
      value: l1.value,
      children: l1.children?.map((product) => ({
        key: `${dept.id}-${l1.id}-${product.id}`,
        name: `${product.name}`,
        value: product.value,
      })),
    })),
  }));

  const getTableTitle = () => {
    if (isCountReport) {
      return 'Consignment Count by Department';
    }
    return 'Earnings by Department';
  };

  return (
    <Table
      title={() => <div style={{ textAlign: 'center', fontWeight: 700 }}>{getTableTitle()}</div>}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      expandable={{ defaultExpandAllRows: true }}
      bordered
      loading={loading}
    />
  );
};

export default ImportExportReportTable;
